import type { Middleware } from "openapi-fetch";

export interface BillingMiddlewareOptions {
  /**
   * Setter called on every tenant-scoped response. Passed a severity
   * string (header value) when the header is present, `null` when absent.
   * Provided by <ApiProvider setBillingWarning={...}> at init time.
   * Mirrors the TokenProvider pattern — api-client never imports from
   * design-system (avoids cycles).
   */
  setBillingWarning: (severity: string | null) => void;
  /**
   * Strict mode — future flip once CC-06 lands in CONTRACT.md. Lenient
   * default (unknown value → severity forwarded, UI maps to "warning").
   */
  strict?: boolean;
}

const BILLING_HEADER = "X-LinkHR-Billing-Warning";
const PLATFORM_URL_SEGMENT = "/api/v1/_platform/";

export function billingMiddleware(opts: BillingMiddlewareOptions): Middleware {
  return {
    onResponse({ request, response }) {
      // Platform (Link Admin) traffic skips billing middleware — linkco
      // staff viewing tenant surfaces don't render banners per D-20.
      if (request.url.includes(PLATFORM_URL_SEGMENT)) return undefined;

      // Dev-only test bridge — mirrors Phase 1's window.__linkhrAuthDev__ pattern.
      // The literal `import.meta.env.DEV` access chain is substituted at Vite
      // transform time; production bundles tree-shake this whole branch.
      // See packages/api-client/src/mocks/handlers/billing-injection.ts for
      // Playwright integration pattern.
      if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV === true) {
        const inject = (globalThis as { __linkhrInjectBilling__?: unknown })
          .__linkhrInjectBilling__;
        if (typeof inject === "string" && inject !== "") {
          opts.setBillingWarning(inject);
          return undefined;
        }
      }

      const header = response.headers.get(BILLING_HEADER);
      // Header absent → tenant is healthy; clear any previously-set warning.
      if (header === null || header === "") {
        opts.setBillingWarning(null);
        return undefined;
      }
      opts.setBillingWarning(header);
      return undefined;
    },
  };
}
